import React from "react";
import { Inline } from "components/common/Stack";
import { MAX_SCALE_EXPONENT, MIN_SCALE_EXPONENT, scaleFactor } from "./types";

interface ScaleExponentInputProps {
  /** Current exponent value. Always visible alongside the chip ladder. */
  value: number;
  onChange: (next: number) => void;
}

const factorFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 12,
});

function formatFactor(exponent: number): string {
  if (exponent === 0) return "×1";
  // Bound by MIN/MAX_SCALE_EXPONENT (±12 inclusive). `toLocaleString` renders
  // `10^-6` (`0.000001`) and `10^12` (`1,000,000,000,000`) cleanly without
  // scientific notation; both are inside JS number precision.
  return `×${factorFormatter.format(scaleFactor(exponent))}`;
}

function clamp(n: number): number {
  return Math.max(MIN_SCALE_EXPONENT, Math.min(MAX_SCALE_EXPONENT, n));
}

/**
 * Always-visible exponent input. Replaces the legacy "Custom…" entry —
 * any integer in `MIN_SCALE_EXPONENT..=MAX_SCALE_EXPONENT` can be typed
 * directly. Picking a shortcut chip writes its exponent into this field;
 * the live factor preview to the right shows the resulting multiplier.
 *
 * - Arrow Up / Arrow Down step by 1 while the input is focused (clamped).
 * - Empty input or stray `-` is permitted mid-keystroke; final value is
 *   clamped on blur back to a valid integer.
 */
export function ScaleExponentInput({
  value,
  onChange,
}: ScaleExponentInputProps) {
  const [draft, setDraft] = React.useState<string>(String(value));

  // Re-sync the visible draft when the external value changes (e.g., user
  // clicked a shortcut chip while the input wasn't focused).
  React.useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (next: number) => {
    const clamped = clamp(next);
    setDraft(String(clamped));
    onChange(clamped);
  };

  return (
    <Inline gap="sm" align="center">
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: "var(--control-height-sm)",
          border: "var(--size-border-hairline) solid var(--color-border)",
          borderRadius: "var(--radius-app)",
          background: "var(--color-bg)",
          overflow: "hidden",
        }}
      >
        <span
          aria-hidden
          style={{
            padding: "0 var(--spacing-xs) 0 var(--spacing-sm)",
            color: "var(--color-fg-muted)",
            fontSize: "var(--font-size-small)",
            fontFamily: "var(--font-mono)",
          }}
        >
          10<sup>n</sup>
        </span>
        <input
          type="text"
          inputMode="numeric"
          aria-label="Scale exponent"
          value={draft}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            // Allow partial input (empty, "-") without committing — commit
            // when we have a real integer.
            const trimmed = raw.trim();
            if (trimmed === "" || trimmed === "-") return;
            const n = parseInt(trimmed, 10);
            if (!Number.isNaN(n)) {
              onChange(clamp(n));
            }
          }}
          onKeyDown={(e) => {
            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
            e.preventDefault();
            const current = Number.isFinite(parseInt(draft, 10))
              ? parseInt(draft, 10)
              : value;
            commit(current + (e.key === "ArrowUp" ? 1 : -1));
          }}
          onBlur={() => {
            const n = parseInt(draft, 10);
            commit(Number.isFinite(n) ? n : 0);
          }}
          style={{
            width: "56px",
            height: "100%",
            border: 0,
            background: "transparent",
            color: "var(--color-fg)",
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--font-size)",
            outline: "none",
            padding: "0 var(--spacing-xs)",
          }}
        />
      </div>
      <span
        aria-live="polite"
        style={{
          color: "var(--color-fg-muted)",
          fontSize: "var(--font-size-small)",
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        = {formatFactor(value)}
      </span>
    </Inline>
  );
}
